<template>
<v-app>
    <AppLayout />

    <ErrorDialog />
    <PlanDialogs />
</v-app>
</template>

<script setup lang="ts">
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { doc, getDoc, getFirestore } from 'firebase/firestore'
import { onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useCurrentUser } from 'vuefire'
import { useDatabaseStore } from '@/stores/database'
import { useFilesStore } from '@/stores/files'
import { useFoldersStore } from '@/stores/folders'
import { usePeopleStore } from '@/stores/people'
import { useUIStore } from '@/stores/ui'
import { usePlanStore } from '@/stores/plan'
import { useRegistryStore } from '@/stores/registry'
import { useUserStore } from '@/stores/user'
import { usePaddle } from '@/composables/usePaddle'
import { usePlanEnforcement } from '@/composables/usePlanEnforcement'
import { postLoginPending } from '@/services/authProvider'
import { logger, loginTime } from '@/utils/logger'
import ErrorDialog from '@/components/ErrorDialog.vue'
import PlanDialogs from '@/components/PlanDialogs.vue'
import AppLayout from '@/layouts/AppLayout.vue'

onMounted(() => {
    // logger.log('App.vue.onMounted')
})

const user = useCurrentUser()
const router = useRouter()
const route = useRoute()
const databaseStore = useDatabaseStore()
const registryStore = useRegistryStore()
const userStore = useUserStore()
const planStore = usePlanStore()
const foldersStore = useFoldersStore()
const filesStore = useFilesStore()
const peopleStore = usePeopleStore()
const uiStore = useUIStore()

usePlanEnforcement()

// Load Paddle.js app-wide so Paddle Retain can run on every page
usePaddle()

// const auth = useFirebaseAuth() // only exists on client side
const auth = getAuth() // only exists on client side

onAuthStateChanged(auth, async user => {
    if (user) {
        // user.metadata.lastSignInTime
        // loginMark('onAuthStateChanged: signed-in user received')

        const fdb = getFirestore()

        try {
            const secretSnap = await loginTime('userSecrets getDoc (Firestore)', () => getDoc(doc(fdb, 'userSecrets', user.uid)))
            const encryptionKey = secretSnap.data()?.encryptionKey as string | undefined
            if (!encryptionKey) {
                throw new Error('Encryption key not found for user')
            }
            userStore.encryptionKey = encryptionKey

            // Initialize database for this user (user.uid is Firebase user ID, NOT Google user ID)
            await databaseStore.initializeDatabase(user.uid, encryptionKey)
            // await loginTime('initializeDatabase (Dexie open)', () => )
        } catch (error) {
            logger.error('Failed to initialize user database:', error)
            databaseStore.failInitialization(error)
            postLoginPending.value = false
            return
        }

        const googleUid = user.providerData.find(p => p.providerId === 'google.com')?.uid ?? null
        userStore.setUser(user.uid, googleUid, user.email)

        // Ensure the registry has this user's Drive 'My Drive' root id
        // await loginTime('ensureMyDriveRootId', () => registryStore.ensureMyDriveRootId())
        await registryStore.ensureMyDriveRootId()
        // await loginTime('planStore.loadLocalState', () => planStore.loadLocalState())
        await planStore.loadLocalState()

        // Logout cleared these stores; a kept-alive page won't re-run its onMounted loading
        await foldersStore.loadFolders()
        await peopleStore.loadPersons()

        // User is signed in, see docs for a list of available properties
        // https://firebase.google.com/docs/reference/js/auth.user
        // logger.log('onAuthStateChanged:', user.uid) // , user
        //         user.providerData.providerData
    } else {
        // logger.log('onAuthStateChanged: User is signed out')
        postLoginPending.value = false
        // Close database when user logs out
        databaseStore.closeDatabase()
        registryStore.clearRegistry()
        planStore.clearLocalState()
        userStore.clearUser()
        foldersStore.resetState()
        filesStore.resetState()
        peopleStore.resetState()
        uiStore.resetState()
    }
})

watch(user, async (currentUser, previousUser) => {
    // redirect to login if they logout and the current
    // route is only for authenticated users
    if (
        !currentUser
        && previousUser
        // && isCurrentRouteAuthenticated(route)
    ) {
        return router.push({ name: '/' })
    }
    // The user just signed in from the login page (or was sent there with a `redirect`
    // query because the auth state wasn't ready yet): move on to the app.
    // Navigating here, with the router injected into the app, instead of from the auth
    // service avoids using a stale router instance (e.g. after a dev HMR reload of the router module).
    if (currentUser && (route.name === '/' || typeof route.query.redirect === 'string')) {
        const redirect = route.query.redirect
        return router.push(typeof redirect === 'string' && redirect.startsWith('/') ? redirect : '/folders')
    }
})
</script>

<style>

 /* @import 'https://cdn.jsdelivr.net/npm/@material-symbols/font-400@0.27.2/outlined.css'; */
</style>
